import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Avatar, 
  Button, 
  TextField, 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Container,
  Paper,
  CssBaseline
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../context/AuthContext";
import { toast } from '../utils/toast';
import Footer from '../components/Footer';
import Logo from '../components/Logo';

// Optimierte LoginPage für besseres Layout
const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [alertOpen, setAlertOpen] = useState(true);
  const navigate = useNavigate();
  const { login, loading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.warning('Bitte gib E-Mail und Passwort ein');
      return;
    }
    
    const success = await login(email, password);
  
    if (success) {
      navigate('/dashboard');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <Box sx={{ 
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        pt: 4, pb: 2
      }}>
        {/* Logo und Titel */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mb: 2 
        }}>
          <Logo height={80} />
          <Typography variant="h4" component="h1" textAlign="center" gutterBottom>
            Meldeportal Trampolin
          </Typography>
        </Box>
        
        {/* Login Card */}
        <Container maxWidth="xs" sx={{ width: '100%' }}>
          <Paper elevation={6} sx={{ 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 3
          }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mb: 1 }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h2" variant="h5" gutterBottom>
              Login
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="E-Mail" 
                autoComplete="email" 
                autoFocus 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                disabled={loading}
              />
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="Passwort" 
                type="password" 
                autoComplete="current-password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                disabled={loading}
              />
              <Button 
                type="submit" 
                fullWidth 
                variant="contained" 
                sx={{ mt: 2, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Einloggen'}
              </Button>
              
              <Collapse in={alertOpen}>
                <Alert 
                  severity="info"
                  icon={<InfoIcon />}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setAlertOpen(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ 
                    mb: 1,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      flexDirection: 'column'
                    }
                  }}
                >
                  <AlertTitle>Hinweis</AlertTitle>
                  Die Anmeldung kann unter Umständen etwas länger dauern, 
                  da sich die App möglicherweise im Ruhezustand befindet und neu starten muss.
                </Alert>
              </Collapse>
            </Box>
          </Paper>
          
          <Box textAlign="center" mb={4}>
            <Typography variant="body2">Noch keinen Account?</Typography>
            <Button variant="outlined" sx={{ mt: 1 }} component={Link} to="/register">
              Registrieren
            </Button>
          </Box>

          <Box mb={4} textAlign="center">
            <Button 
              component={Link} 
              to="/forgot-password" 
              color="primary"
              sx={{ 
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Passwort vergessen?
            </Button>
          </Box>
        </Container>
      </Box>
      
      {/* Footer - volle Breite und kein zusätzlicher Abstand */}
      <Box sx={{ 
        width: '100%',
        mt: 'auto'
      }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default LoginPage;